import {
  resolveSelection,
  toSelectionParam,
} from "src/components/selection-filter/selection-filter";
import { useSearchParams } from "src/router";
import { parseCommaList, serializeCommaList } from "src/utils/comma-list";

/**
 * A Knowledge filter whose every option starts checked, held in one URL param.
 * The value is `null` when unfiltered and `[]` when nothing is selected, which
 * matches no rows. Selecting every option removes the param, so "All" keeps a
 * clean URL and still means All after an option is added.
 */
export function useSelectionFilterParam<T extends string>(
  parameter: string,
  options: readonly T[],
): [T[] | null, (next: T[]) => void] {
  const [raw, setRaw] = useSelectionParam(parameter);
  return [
    resolveSelection(raw, options),
    (next) => setRaw(toSelectionParam(next, options)),
  ];
}

/**
 * A selection's raw URL param: absent is `null` and `?param=` is `[]`, unlike
 * `useMultiQueryState`, which can't tell them apart. Writing `null` removes it.
 */
export function useSelectionParam(
  parameter: string,
): [string[] | null, (next: string[] | null) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get(parameter);
  const raw = param === null ? null : parseCommaList(param);

  const setRaw = (next: string[] | null) => {
    // Replaces the location like `useMultiQueryState`, which Knowledge's
    // `/new` draft relies on to see a filter change.
    setSearchParams(
      (params) => {
        const updated = new URLSearchParams(params);
        if (next === null) {
          updated.delete(parameter);
        } else {
          updated.set(parameter, serializeCommaList(next));
        }
        return updated;
      },
      { replace: true },
    );
  };
  return [raw, setRaw];
}
